import React, { FC, useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Table, TableRow } from '@code/innovance-react-ui-kit';
import styled from 'styled-components';
import { useCustomerSearchState } from 'src/front/contexts/ViewCreditsContext';
import {
  Title,
  InfoRow,
  InfoLabel,
  FormRow,
  LineSeparator,
  Subtitle,
  Separator,
} from 'src/front/components/FormStyles';
import { CreditView } from 'src/front/api/apiInstance';
import { TABLE_WIDTHS, BREAK_POINTS } from 'src/front/constant';

export const TableWrapper = styled.div`
  width: auto;

  @media screen and (min-width: ${BREAK_POINTS.MIN.LARGE_2X}) {
    width: 1070px;
    overflow-x: scroll;
  }
  @media screen and (max-width: ${BREAK_POINTS.MAX.LARGE_2X}) and (min-width: ${BREAK_POINTS.MIN
      .LARGE_1X}) {
    width: 970px;
    overflow-x: scroll;
  }
  @media screen and (max-width: ${BREAK_POINTS.MAX.LARGE_1X}) and (min-width: ${BREAK_POINTS.MIN
      .TABLET}) {
    width: 870px;
    overflow-x: scroll;
  }
  @media screen and (max-width: ${BREAK_POINTS.MAX.TABLET}) and (min-width: ${BREAK_POINTS.MIN
      .PHONE}) {
    width: 600px;
    overflow-x: scroll;
  }
`;

const creditResultTableHeaders = [
  'general_status_label',
  'general_components_fullName_text',
  'general_components_contractNumber_text',
  'general_currency_label',
  'general_components_loanAmount_text',
  'general_date_label',
  'viewCredits_maxNumberDelayDay_text',
  'general_components_loanType_label',
];
const ViewCreditResults: FC = () => {
  const { customerNumber, customerSearchValues } = useCustomerSearchState();
  const { t } = useTranslation();
  const [hasCreditNextPage, setHasCreditNextPage] = useState<boolean>(false);
  const [creditList, setCreditList] = useState<TableRow[]>([]);
  const [creditsLoading, setCreditsLoading] = useState<boolean>(false);
  const [creditPage, setCreditPage] = useState<number>(1);

  const loadCreditList = useCallback(
    async (pageNumber = creditPage, forceRefresh = false): Promise<void> => {
      try {
        setCreditsLoading(true);
        const { data } = await CreditView.getCreditsDetails({
          customerNumber: customerNumber || '',
          pageNumber: pageNumber || creditPage,
          refresh: !pageNumber || forceRefresh,
          rowCountPerPage: 15,
        });

        const credits = data.creditList.map((credit, index) => ({
          id: index.toString(),
          columns: [
            credit.status,
            credit.fullName,
            credit.contractNumber,
            credit.currency,
            credit.loanAmount,
            credit.date,
            credit.maximumNumberOfDelayDays,
            credit.loanType,
          ],
        }));
        setCreditList(credits);
        setHasCreditNextPage(data.hasNextPage);
        setCreditPage(pageNumber || 1);
        setCreditsLoading(false);
      } catch (error) {
        // TODO (ozgunbal): show error on modal or handle with error boundary
      }
    },
    [creditPage, customerNumber]
  );
  useEffect(() => {
    loadCreditList(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <Title>
        <Trans i18nKey="viewCredits_title" />
      </Title>
      <Separator />
      <InfoRow>
        <InfoLabel>
          <Trans i18nKey="general_components_clientName_text" />:
        </InfoLabel>
        {`${customerSearchValues?.customerName} ${customerSearchValues?.customerSecondName} ${customerSearchValues?.customerSurname}`}
      </InfoRow>
      <Separator />
      <FormRow>
        <LineSeparator />
      </FormRow>
      <Separator />
      <Subtitle>
        <Trans i18nKey="viewCredits_viewApplications_text" />
      </Subtitle>
      <Separator />
      <TableWrapper>
        <Table
          emptyTableText={t('general_components_noData_errorMessage')}
          hasNextPage={hasCreditNextPage}
          headers={creditResultTableHeaders.map((head) => t(head))}
          items={creditList}
          loading={creditsLoading}
          loadPageItems={loadCreditList}
          pageNumber={creditPage}
          tableWidth={TABLE_WIDTHS.DESKTOP_2X}
          widths={['11', '15', '15', '11', '11', '11', '11', '15']}
          clearItemOnPageChange
        />
      </TableWrapper>
    </>
  );
};

export default ViewCreditResults;
